import { IAppContext } from 'App/AppContext';
import { mapFromApprovals } from 'mappings/approvalMapping';
import { mapFromOutlookTasks } from 'mappings/outlook/outlookTasksMapping';
import {
  ITaskMappingOptions,
  mapFromDateRange,
  mapFromTask,
  mapFromTaskCheckListConversion,
  mapFromTaskContext,
  mapFromTaskLookupDTO,
  mapFromTaskRelation,
  mapFromTasks,
  mapFromTasksArray,
  mapFromTaskTemplateScene,
  mapToTask,
  mapToTasks,
} from 'mappings/taskMapping';
import { mapFromTaskType, mapFromTaskTypeForm, mapFromTaskTypes, mapToTaskType } from 'mappings/taskTypeMapping';
import {
  mapFromFormStructureMutationResult,
  mapFromFormArchivePreview,
  mapFromFormPurgePreview,
  mapFromFormUpgradePreview,
  mapFromFormUpgradeResult,
  mapFromFormVersionDiff,
  mapFromFormVersionHistory,
  mapFromTextCorrectionImpactPreview,
  mapToFormArchiveMutation,
  mapToFormEditSessionDiscardMutation,
  mapToFormPurgeMutation,
  mapToFormStructureMutation,
  mapToFormTextCorrectionMutation,
  mapToFormUpgradeMutation,
} from 'mappings/formVersionMapping';
import { mapFromEvidenceLocation } from 'mappings/outputLocationBindingMapping';
import { Approval } from 'models/approval';
import DateRange from 'models/dateRange';
import AssigneeDTO from 'models/dto/assigneeDTO';
import DateRangeDTO from 'models/dto/dateRangeDTO';
import { EvidenceLocationDTO } from 'models/dto/evidenceLocationDTO';
import IdListDTO from 'models/dto/IdListDTO';
import EvidenceLocation from 'models/evidenceLocation';
import OutlookTasksDTO from 'models/dto/outlook/outlookTasks';
import AuditEntityTasksDTO from 'models/dto/tasks/auditEntityTasksDTO';
import { TaskCheckListConversionDTO } from 'models/dto/tasks/taskChecklistConversionDTO';
import { TaskContextDTO } from 'models/dto/tasks/taskContextDTO';
import TaskDTO from 'models/dto/tasks/taskDTO';
import TaskLookupDTO from 'models/dto/tasks/taskLookupDTO';
import { TaskRelationDTO } from 'models/dto/tasks/taskRelationDTO';
import TasksDTO from 'models/dto/tasks/tasksDTO';
import TaskTemplateSceneDTO from 'models/dto/tasks/taskTemplateSceneDTO';
import { TaskTypeConversionDTO } from 'models/dto/tasks/taskTypeConversionDTO';
import TaskTypeDTO from 'models/dto/tasks/taskTypeDTO';
import TaskTypeFormElementDTO from 'models/dto/tasks/taskTypeFormElementDTO';
import {
  FormStructureMutationResultDTO,
  FormArchivePreviewDTO,
  FormPurgePreviewDTO,
  FormTextCorrectionMutationDTO,
  FormUpgradePreviewDTO,
  FormUpgradeResultDTO,
  TextCorrectionImpactPreviewDTO,
  FormVersionDiffDTO,
  FormVersionHistoryDTO,
} from 'models/dto/tasks/formVersionDTO';
import Entity, { EntityTypes } from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import OutlookTasks from 'models/outlook/outlookTasks';
import RecurringPattern from 'models/recurringPattern';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskCheckListConversion } from 'models/tasks/taskChecklistConversion';
import { TaskContext } from 'models/tasks/taskContext';
import { TaskRelation, TaskRelationship } from 'models/tasks/taskRelation';
import Tasks from 'models/tasks/tasks';
import TaskTemplateScene from 'models/tasks/taskTemplateScene';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import {
  FormStructureMutationResult,
  FormArchivePreview,
  FormPurgePreview,
  FormTextCorrectionMutation,
  FormUpgradePreview,
  FormUpgradeResult,
  FormVersionDiff,
  FormVersionHistory,
  TextCorrectionImpactPreview,
} from 'models/tasks/formVersion';
import AppError from 'utils/appError';
import { toApiDateRequired, toApiDateTimeOptional, toApiDateTimeRequired } from 'utils/datetime';
import { newGuid } from 'utils/guid';
import http from './httpService';

export async function apiAddTask(
  task: Task,
  schedule: boolean,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  year?: number,
  options?: ITaskMappingOptions,
  suppressAssignmentNotifications = false,
): Promise<Task> {
  try {
    const taskDTO = mapToTask(task, options);
    const ar = await http.post<TaskDTO>(
      `/tasks?schedule=${schedule}&year=${
        year ?? 0
      }&suppressAssignmentNotifications=${suppressAssignmentNotifications}`,
      taskDTO,
      http.getRequestConfig(accessToken),
    );

    return mapFromTask(ar.data, globalDataCache) as Task;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskTemplateIdByName(name: string, accessToken: string): Promise<number | undefined> {
  try {
    const response = await http.get<number>(
      `/tasks/templates/idbyname?name=${encodeURIComponent(name)}`,
      http.getRequestConfig(accessToken),
    );

    return response.status === 204 ? undefined : response.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTask(
  task: Task,
  schedule: boolean,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  year?: number,
  options?: ITaskMappingOptions,
  suppressAssignmentNotifications = false,
): Promise<Task | undefined> {
  try {
    const taskDTO = mapToTask(task, options);
    const ar = await http.put<TaskDTO>(
      `/tasks?schedule=${schedule}&year=${
        year ?? 0
      }&suppressAssignmentNotifications=${suppressAssignmentNotifications}`,
      taskDTO,
      http.getRequestConfig(accessToken),
    );

    if (ar.data) {
      return mapFromTask(ar.data, globalDataCache);
    } else {
      return undefined;
    }
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTasks(
  tasks: Task[],
  schedule: boolean,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  year?: number,
  suppressAssignmentNotifications = false,
): Promise<Tasks> {
  try {
    const tasksDTO = mapToTasks(tasks);
    const ar = await http.put<TasksDTO>(
      `/tasks/bulk?schedule=${schedule}&year=${
        year ?? 0
      }&suppressAssignmentNotifications=${suppressAssignmentNotifications}`,
      tasksDTO,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTask(task: Task, accessToken: string): Promise<void> {
  try {
    await http.post(`/tasks/delete/${task.taskId}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForControl(
  controlId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/control/${controlId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForAuditEntity(
  entityType: EntityTypes,
  entityId: number,
  accessToken: string,
): Promise<number[]> {
  try {
    const ar = await http.get<AuditEntityTasksDTO>(
      `/tasks/internalaudit/${entityType}/${entityId}`,
      http.getRequestConfig(accessToken),
    );

    return ar.data.taskIds;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForTag(
  tagId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(`/tasks/tag/${tagId}`, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForTheme(
  themeId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/theme/${themeId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForRisk(
  riskId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/risk/${riskId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForRiskViewSchedule(
  riskId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/risk/${riskId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForControlMonitoring(
  controlId: number,
  startDate: Date | undefined,
  endDate: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(`/tasks/control/${controlId}/monitor`, {
      ...http.getRequestConfig(accessToken),
      params: {
        startDate: toApiDateTimeOptional(startDate),
        endDate: toApiDateTimeOptional(endDate),
      },
    });

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForThemeAudit(
  themeId: number,
  startDate: Date | undefined,
  endDate: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(`/tasks/theme/${themeId}/monitor`, {
      ...http.getRequestConfig(accessToken),
      params: {
        startDate: toApiDateTimeOptional(startDate),
        endDate: toApiDateTimeOptional(endDate),
      },
    });

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForProcess(
  processId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/process/${processId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForProcessViewSchedule(
  processId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/process/${processId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(
        end,
      )}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForObjective(
  objectiveId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/objective/${objectiveId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForObjectiveViewSchedule(
  objectiveId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/objective/${objectiveId}/schedule?startDate=${toApiDateTimeRequired(
        start,
      )}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForAsset(
  assetId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/asset/${assetId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForAssetViewSchedule(
  assetId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/asset/${assetId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(
        end,
      )}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForOwner(
  ownerId: string,
  includeCompleted: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/owner/${ownerId}?includeCompleted=${includeCompleted}`;
    if (start) url += `&startDate=${toApiDateTimeOptional(start)}`;
    if (end) url += `&endDate=${toApiDateTimeOptional(end)}`;

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForOwnerViewSchedule(
  ownerId: string,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/owner/${ownerId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(
        end,
      )}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForUserViewSchedule(
  userId: string,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/user/${userId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForControlViewSchedule(
  controlId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/control/${controlId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(
        end,
      )}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForThemeViewSchedule(
  themeId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/theme/${themeId}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(
        end,
      )}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetMonitoringTasksViewSchedule(
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetMonitoringTasksForIdViewSchedule(
  id: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/${id}/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForUser(
  userId: string,
  includeCompleted: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  projectId?: number,
): Promise<Tasks> {
  try {
    let url = `/tasks/user/${userId}?includeCompleted=${includeCompleted}`;
    if (start) url += `&startDate=${toApiDateTimeOptional(start)}`;
    if (end) url += `&endDate=${toApiDateTimeOptional(end)}`;
    if (projectId) url += `&projectId=${projectId}`;

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForUsers(
  userIds: string[],
  includeCompleted: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const body: IdListDTO = new IdListDTO([...new Set(userIds)]); //remove duplicates

    let url = `/tasks/users?includeCompleted=${includeCompleted}`;
    if (start) url += `&startDate=${toApiDateTimeOptional(start)}`;
    if (end) url += `&endDate=${toApiDateTimeOptional(end)}`;

    const ar = await http.post<TasksDTO>(url, body, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForUsersViewSchedule(
  userIds: string[],
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const body: IdListDTO = new IdListDTO([...new Set(userIds)]); //remove duplicates
    const ar = await http.post<TasksDTO>(
      `/tasks/users/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(end)}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskForEvent(
  eventId: string,
  eventDate: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<OutlookTasks> {
  try {
    const apiEventDate: string = toApiDateTimeRequired(eventDate);
    const ar = await http.get<OutlookTasksDTO>(
      `/tasks/event/${eventId}/${apiEventDate}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromOutlookTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasks(
  includeCompleted: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks?includeCompleted=${includeCompleted}`;
    if (start) url += `&startDate=${toApiDateTimeOptional(start)}`;
    if (end) url += `&endDate=${toApiDateTimeOptional(end)}`;

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskLookup(
  includeCompleted: boolean,
  taskType: number | undefined,
  excludeSpecialPurpose: boolean,
  accessToken: string,
): Promise<Entity[]> {
  try {
    let url = `/tasks/lookup?includeCompleted=${includeCompleted}&excludeSpecialPurpose=${excludeSpecialPurpose}`;
    if (taskType !== undefined) url += `&taskType=${taskType}`;

    const ar = await http.get<TaskLookupDTO[]>(url, http.getRequestConfig(accessToken));

    return (ar.data ?? []).map(mapFromTaskLookupDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksViewSchedule(
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/all/schedule?startDate=${toApiDateTimeRequired(start)}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForId(
  taskIds: number[],
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const idList = new IdListDTO(taskIds.map((i) => i.toString()));
    const ar = await http.post<TasksDTO>(`/tasks/id`, idList, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetSingleTask(
  taskId: number,
  preferSeries: boolean,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `tasks/${taskId}?preferSeries=${preferSeries}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTaskKanbanState(task: Task, accessToken: string): Promise<Task> {
  try {
    const assignee: AssigneeDTO = new AssigneeDTO();

    await http.put<TaskDTO>(
      `/tasks/${task.taskId}/?taskState=${task.taskStateId}&sortOrder=${task.sortOrder}`,
      assignee,
      http.getRequestConfig(accessToken),
    );

    return task;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTaskKanbanAssignee(task: Task, accessToken: string): Promise<Task> {
  try {
    let assignee: AssigneeDTO;

    if (task.user) {
      assignee = new AssigneeDTO(task.user.id, task.user.name, task.user.email, 'user');
    } else {
      assignee = new AssigneeDTO();
    }

    await http.put<TaskDTO>(
      `/tasks/${task.taskId}/?sortOrder=${task.sortOrder}`,
      assignee,
      http.getRequestConfig(accessToken),
    );

    return task;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateTaskEvent(task: Task, accessToken: string): Promise<string> {
  try {
    const taskDTO = mapToTask(task);
    const ar = await http.post<string>(`/tasks/event/${task.taskId}`, taskDTO, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTaskEvent(task: Task, accessToken: string): Promise<void> {
  try {
    const taskDTO = mapToTask(task);
    await http.post<TaskDTO>(`/tasks/event/delete/${task.taskId}`, taskDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTasksEvent(tasks: Task[], accessToken: string): Promise<void> {
  try {
    const idList = new IdListDTO(tasks.map((t) => t.taskId.toString()));
    await http.post<TaskDTO>(`/tasks/event/delete/bulk`, idList, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTaskCompleted(task: Task, accessToken: string): Promise<void> {
  try {
    const taskDTO = mapToTask(task);
    await http.put<TaskDTO>(`/tasks/completed`, taskDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTaskChecklist(task: Task, accessToken: string): Promise<void> {
  try {
    const taskDTO = mapToTask(task);
    await http.put<TaskDTO>('/tasks/checklist', taskDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskTemplates(
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<TaskTemplateScene> {
  try {
    const ar = await http.get<TaskTemplateSceneDTO>(`/tasks/templates`, http.getRequestConfig(accessToken));

    return mapFromTaskTemplateScene(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetActiveTaskTemplates(
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Task[]> {
  try {
    const ar = await http.get<TaskDTO[]>(`/tasks/templates/active`, http.getRequestConfig(accessToken));

    return mapFromTasksArray(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskInstances(
  taskId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Task[]> {
  try {
    const ar = await http.get<TaskDTO[]>(`/tasks/${taskId}/instances`, http.getRequestConfig(accessToken));

    return mapFromTasksArray(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskInstancesByCount(
  taskId: number,
  count: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Task[]> {
  try {
    const ar = await http.get<TaskDTO[]>(
      `/tasks/${taskId}/instances/schedule?count=${count}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasksArray(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskInstancesByDate(
  taskId: number,
  start: Date,
  end: Date,
  count: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Task[]> {
  try {
    const ar = await http.get<TaskDTO[]>(
      `/tasks/${taskId}/instances/schedule?start=${toApiDateRequired(start)}&end=${toApiDateRequired(
        end,
      )}&count=${count}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasksArray(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskRescheduleRange(taskId: number, accessToken: string): Promise<DateRange> {
  try {
    const ar = await http.get<DateRangeDTO>(`/tasks/${taskId}/reschedule`, http.getRequestConfig(accessToken));

    return mapFromDateRange(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiConvertTaskChecklistToForm(
  taskId: number,
  trans_checklist: string,
  trans_checklist_item: string,
  accessToken: string,
): Promise<TaskCheckListConversion | undefined> {
  try {
    const dto = new TaskCheckListConversionDTO();
    dto.taskId = taskId;
    dto.trans_FormSuffix = trans_checklist;
    dto.trans_KPISuffix = trans_checklist_item;

    const ar = await http.post<TaskCheckListConversionDTO>(
      `/tasks/checklist/convert`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromTaskCheckListConversion(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiConvertTaskType(
  taskId: number,
  targetTaskType: TaskTypes,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  recurringPattern?: RecurringPattern,
): Promise<Task | undefined> {
  try {
    const dto = new TaskTypeConversionDTO();
    dto.taskId = taskId;
    dto.targetTaskType = targetTaskType;
    dto.recurringPattern = recurringPattern?.toJSON();

    const ar = await http.post<TaskDTO>(`/tasks/type/convert`, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTask(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateTaskSchedule(
  auditYear: number,
  removeOnly: boolean,
  accessToken: string,
  taskIds?: number[],
): Promise<void> {
  try {
    await http.post(
      `/tasks/createschedule?year=${auditYear}&removeOnly=${removeOnly}`,
      taskIds ?? null,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCompleteTaskSchedule(accessToken: string): Promise<void> {
  try {
    await http.post(`/tasks/completeschedule`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWebLinkForEvent(task: Task, accessToken: string): Promise<string> {
  try {
    const ar = await http.get(`tasks/event/weblink/${task.taskId}`, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export const apiGetTaskRelations = async (
  taskId: number,
  accessToken: string,
  globalCache: GlobalDataCache,
): Promise<TaskRelation[]> => {
  try {
    const ar = await http.get<TaskRelationDTO[]>(`/tasks/relations/${taskId}`, http.getRequestConfig(accessToken));

    return (ar.data ?? []).map((r) => mapFromTaskRelation(r, globalCache));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

/**
 * Relates one task to another: "taskIdFrom has this follow-up". Idempotent server-side, so a repeat
 * of the same relation is a no-op rather than an error.
 */
export const apiAddTaskRelation = async (
  taskIdFrom: number,
  taskIdTo: number,
  relationship: TaskRelationship,
  accessToken: string,
): Promise<void> => {
  try {
    const dto = new TaskRelationDTO();
    dto.taskIdFrom = taskIdFrom;
    dto.taskIdTo = taskIdTo;
    dto.relationship = relationship;

    await http.post<TaskRelationDTO>(`/tasks/relation`, dto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetCompletedMonitoringTasks = async (
  accessToken: string,
  globalCache: GlobalDataCache,
): Promise<Tasks> => {
  try {
    const ar = await http.get<TasksDTO>(`/tasks/schedule/completed`, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetTaskTypes = async (accessToken: string): Promise<TaskType[]> => {
  try {
    const ar = await http.get<TaskTypeDTO[]>(`/tasks/types`, http.getRequestConfig(accessToken));

    return mapFromTaskTypes(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetVersionedForms = async (includeArchived: boolean, accessToken: string): Promise<TaskType[]> => {
  try {
    const ar = await http.get<TaskTypeDTO[]>(
      `/tasks/forms?includeArchived=${includeArchived}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTaskTypes(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiAddTaskType = async (taskType: TaskType, accessToken: string): Promise<TaskType> => {
  try {
    const taskTypeDTO = mapToTaskType(taskType);
    const ar = await http.post<TaskTypeDTO>(`/tasks/types`, taskTypeDTO, http.getRequestConfig(accessToken));

    return mapFromTaskType(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiUpdateTaskType = async (taskType: TaskType, accessToken: string): Promise<TaskType> => {
  try {
    const taskTypeDTO = mapToTaskType(taskType);
    const ar = await http.put<TaskTypeDTO>(`/tasks/types`, taskTypeDTO, http.getRequestConfig(accessToken));

    return mapFromTaskType(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiUpdateFormStructure = async (
  taskType: TaskType,
  formElements: TaskTypeFormElement[],
  editSessionId: string,
  accessToken: string,
  mutationId = newGuid(),
): Promise<FormStructureMutationResult> => {
  try {
    if (!taskType.taskTypeFamilyId) {
      const fallbackTaskType = taskType.clone();
      fallbackTaskType.form = formElements;
      const updatedTaskType = await apiUpdateTaskType(fallbackTaskType, accessToken);
      const fallbackResult = new FormStructureMutationResult();
      fallbackResult.taskType = updatedTaskType;

      return fallbackResult;
    }

    const mutation = mapToFormStructureMutation(taskType, formElements, mutationId, editSessionId);
    const ar = await http.put<FormStructureMutationResultDTO>(
      '/tasks/forms/structure',
      mutation,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormStructureMutationResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiDiscardFormEditSession = async (
  taskType: TaskType,
  baselineDefinitionHash: string,
  editSessionId: string,
  accessToken: string,
  mutationId = newGuid(),
): Promise<FormStructureMutationResult> => {
  try {
    const mutation = mapToFormEditSessionDiscardMutation(taskType, baselineDefinitionHash, mutationId, editSessionId);
    const ar = await http.post<FormStructureMutationResultDTO>(
      '/tasks/forms/structure/discard',
      mutation,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormStructureMutationResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetFormVersionHistory = async (
  taskTypeFamilyId: number,
  accessToken: string,
): Promise<FormVersionHistory[]> => {
  try {
    const ar = await http.get<FormVersionHistoryDTO[]>(
      `/tasks/forms/${taskTypeFamilyId}/history`,
      http.getRequestConfig(accessToken),
    );

    return ar.data.map((item) => mapFromFormVersionHistory(item));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetFormVersion = async (
  taskTypeFamilyId: number,
  taskTypeId: number,
  accessToken: string,
): Promise<TaskType> => {
  try {
    const ar = await http.get<TaskTypeDTO>(
      `/tasks/forms/${taskTypeFamilyId}/versions/${taskTypeId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTaskType(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiCompareFormVersions = async (
  taskTypeFamilyId: number,
  taskTypeId: number,
  compareTaskTypeId: number,
  accessToken: string,
): Promise<FormVersionDiff> => {
  try {
    const ar = await http.get<FormVersionDiffDTO>(
      `/tasks/forms/${taskTypeFamilyId}/compare/${taskTypeId}/${compareTaskTypeId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormVersionDiff(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetFormArchivePreview = async (
  taskTypeFamilyId: number,
  accessToken: string,
): Promise<FormArchivePreview> => {
  try {
    const ar = await http.get<FormArchivePreviewDTO>(
      `/tasks/forms/${taskTypeFamilyId}/archivepreview`,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormArchivePreview(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiArchiveForm = async (taskTypeFamilyId: number, accessToken: string): Promise<void> => {
  try {
    const mutation = mapToFormArchiveMutation(taskTypeFamilyId);
    await http.post('/tasks/forms/archive', mutation, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetFormUpgradePreview = async (
  taskTypeFamilyId: number,
  taskMasterId: number | undefined,
  accessToken: string,
): Promise<FormUpgradePreview> => {
  try {
    const query = taskMasterId !== undefined ? `?taskMasterId=${taskMasterId}` : '';
    const ar = await http.get<FormUpgradePreviewDTO>(
      `/tasks/forms/${taskTypeFamilyId}/upgradepreview${query}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormUpgradePreview(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiUpgradeFormTasks = async (
  taskTypeFamilyId: number,
  taskMasterId: number | undefined,
  accessToken: string,
): Promise<FormUpgradeResult> => {
  try {
    const mutation = mapToFormUpgradeMutation(taskTypeFamilyId, taskMasterId);
    const ar = await http.post<FormUpgradeResultDTO>(
      '/tasks/forms/upgrade',
      mutation,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormUpgradeResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

// Moves one task to the current version of its form and returns that version. A 409 carries a
// FormVersionConflictCode in the error data (closed, answered, not a consumer); the task keeps its pin.
export const apiUpgradeTaskFormVersion = async (taskId: number, accessToken: string): Promise<TaskType> => {
  try {
    const ar = await http.post<TaskTypeDTO>(`/tasks/${taskId}/form/upgrade`, null, http.getRequestConfig(accessToken));

    return mapFromTaskType(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiUnarchiveForm = async (taskTypeFamilyId: number, accessToken: string): Promise<void> => {
  try {
    await http.post(`/tasks/forms/${taskTypeFamilyId}/unarchive`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiPurgeFormVersion = async (
  taskTypeFamilyId: number,
  taskTypeId: number,
  accessToken: string,
): Promise<void> => {
  try {
    const mutation = mapToFormPurgeMutation(taskTypeFamilyId, taskTypeId, newGuid());
    await http.post('/tasks/forms/purge', mutation, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetFormPurgePreview = async (
  taskTypeFamilyId: number,
  taskTypeId: number,
  accessToken: string,
): Promise<FormPurgePreview> => {
  try {
    const response = await http.get<FormPurgePreviewDTO>(
      `/tasks/forms/${taskTypeFamilyId}/versions/${taskTypeId}/purgepreview`,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormPurgePreview(response.data);
  } catch (error) {
    throw AppError.fromApiError(error);
  }
};

export const apiCorrectFormText = async (
  mutation: FormTextCorrectionMutation,
  accessToken: string,
): Promise<TaskType> => {
  try {
    const dto: FormTextCorrectionMutationDTO = mapToFormTextCorrectionMutation(mutation);
    const ar = await http.post<TaskTypeDTO>('/tasks/forms/correct', dto, http.getRequestConfig(accessToken));

    return mapFromTaskType(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiPreviewFormTextCorrection = async (
  mutation: FormTextCorrectionMutation,
  accessToken: string,
): Promise<TextCorrectionImpactPreview> => {
  try {
    const dto: FormTextCorrectionMutationDTO = mapToFormTextCorrectionMutation(mutation);
    const response = await http.post<TextCorrectionImpactPreviewDTO>(
      '/tasks/forms/correct/preview',
      dto,
      http.getRequestConfig(accessToken),
    );

    return mapFromTextCorrectionImpactPreview(response.data);
  } catch (error) {
    throw AppError.fromApiError(error);
  }
};

export const apiRemoveTaskType = async (id: number, accessToken: string): Promise<void> => {
  try {
    await http.post<TaskTypeDTO>(`/tasks/types/delete/${id}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetTaskTypeForm = async (taskTypeId: number, accessToken: string): Promise<TaskTypeFormElement[]> => {
  try {
    const ar = await http.get<TaskTypeFormElementDTO[]>(
      `/tasks/types/${taskTypeId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTaskTypeForm(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetTaskTypeFormAndData = async (
  taskTypeId: number,
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<TaskTypeFormElement[]> => {
  try {
    const ar = await http.get<TaskTypeFormElementDTO[]>(
      `/tasks/types/${taskTypeId}/${entityType}/${entityId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTaskTypeForm(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetTasksForTypeApproval = async (
  taskType: TaskType,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Approval[]> => {
  try {
    const ar = await http.get<TaskDTO[]>(
      `/tasks/types/${taskType.taskTypeId}/approval`,
      http.getRequestConfig(accessToken),
    );

    return mapFromApprovals(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetTaskApprovalComments = async (
  taskId: number,
  accessToken: string,
): Promise<Record<number, string>> => {
  try {
    const ar = await http.get<Record<number, string>>(
      `/tasks/${taskId}/approval/comments`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiGetTaskContext = async (
  taskId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<TaskContext> => {
  try {
    const ar = await http.get<TaskContextDTO>(`/tasks/context/${taskId}`, http.getRequestConfig(accessToken));

    return mapFromTaskContext(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export async function apiGetTaskTypeName(name: string, accessToken: string): Promise<number> {
  try {
    const ar = await http.post<number>(`/tasks/types/name`, { valueString: name }, http.getRequestConfig(accessToken));
    const id = Number(ar.data);

    return id;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiOutlookTestPermissions(accessToken: string): Promise<void> {
  try {
    await http.get<boolean>(`/tasks/event/haspermissions`, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForProcessingActivity(
  processingActivityId: number,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    let url = `/tasks/processingActivity/${processingActivityId}`;
    if (start || end) {
      url += '?';
      if (start) url += `start=${toApiDateTimeOptional(start)}`;
      if (start && end) url += '&';
      if (end) url += `end=${toApiDateTimeOptional(end)}`;
    }

    const ar = await http.get<TasksDTO>(url, http.getRequestConfig(accessToken));

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTasksForProcessingActivityViewSchedule(
  processingActivityId: number,
  start: Date,
  end: Date,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Tasks> {
  try {
    const ar = await http.get<TasksDTO>(
      `/tasks/processingActivity/${processingActivityId}/schedule?startDate=${toApiDateTimeRequired(
        start,
      )}&endDate=${toApiDateTimeRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTasks(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTaskExternalId(accessToken: string, externalId: string): Promise<number> {
  try {
    const ar = await http.post<number>(
      `/tasks/externalid`,
      { valueString: externalId },
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Where the task's evidence is uploaded now: its template's current binding, else the tenant's. With ensure the server
 * gets or creates the task's folder by name when the rule makes one.
 */
export async function apiGetTaskEvidenceLocation(
  taskId: number,
  ensure: boolean,
  accessToken: string,
): Promise<EvidenceLocation> {
  try {
    const ar = await http.get<EvidenceLocationDTO>(
      `/tasks/${taskId}/evidence-location?ensure=${ensure}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromEvidenceLocation(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
